import {observer} from "mobx-react"
import {Checkbox, FieldInput, FieldSelect, FormSection} from "../../ui"
import {countryStore, validationStore} from "../../../store"


export interface CmsFormData {
    active: boolean
    customerNumber: string
    displayName: string
    name: string
    addressAddition: string
    addressCity: string
    addressCountry: string
    addressHouseNumber: string
    addressPostCode: string
    addressStreet: string
}

interface CmsFormProps {
    data: CmsFormData
    mode: 'create' | 'update',
    saving: boolean
    onChange: (value: CmsFormData) => any
    validate: () => Promise<void>
}


const CmsForm = observer(function CmsForm({
                                              data,
                                              saving,
                                              mode,
                                              onChange,
                                              validate
                                          }: CmsFormProps) {
    const {
        name,
        customerNumber,
        displayName,
        active,
        addressHouseNumber,
        addressAddition,
        addressCountry,
        addressPostCode,
        addressCity,
        addressStreet,
    } = data

    const nameHelp = validationStore.getFor('data.name')
    const displayNameHelp = validationStore.getFor('data.displayName')
    const customerNumberHelp = validationStore.getFor('data.customerNumber')

    const addressStreetHelp = validationStore.getFor('data.address.street')
    const addressHouseNumberHelp = validationStore.getFor('data.address.houseNumber')
    const addressAdditionHelp = validationStore.getFor('data.address.addition')
    const addressPostCodeHelp = validationStore.getFor('data.address.postCode')
    const addressCityHelp = validationStore.getFor('data.address.city')
    const addressCountryHelp = validationStore.getFor('data.address.country')

    return <div style={{flex: '1', overflow: 'auto', padding: '1em'}}>

        <FormSection
            label={"Allgemeines"}
            children={
                <div style={{paddingBottom: '1em'}}>
                    <div className={"columns"}>
                        <div className={"column is-one-third"}>
                            <FieldInput
                                label={"Name"}
                                disabled={saving}
                                required={true}
                                invalid={Boolean(nameHelp)}
                                help={nameHelp}
                                value={name}
                                onBlur={async () => await validate()}
                                onChange={name => {
                                    onChange({
                                        ...data,
                                        name
                                    })
                                }}
                            />
                        </div>
                        <div className={"column is-one-third"}>
                            <FieldInput
                                label={"Kundennummer (SAP)"}
                                disabled={saving || mode === 'update'}
                                required={true}
                                invalid={Boolean(customerNumberHelp)}
                                help={customerNumberHelp}
                                value={customerNumber}
                                onBlur={async () => await validate()}
                                onChange={customerNumber => {
                                    onChange({
                                        ...data,
                                        customerNumber
                                    })
                                }}
                            />
                        </div>
                        <div className={"column is-one-third"}>
                            <FieldInput
                                label={"Anzeigename"}
                                disabled={saving}
                                required={true}
                                invalid={Boolean(displayNameHelp)}
                                help={displayNameHelp}
                                value={displayName}
                                onBlur={async () => await validate()}
                                onChange={displayName => {
                                    onChange({
                                        ...data,
                                        displayName
                                    })
                                }}
                            />
                        </div>
                    </div>
                    <div className={"columns"}>
                        <div className={"column is-one-third"}>
                            <Checkbox
                                label={" Aktiv"}
                                disabled={saving}
                                value={active}
                                onBlur={async () => await validate()}
                                onChange={active => {
                                    onChange({
                                        ...data,
                                        active
                                    })
                                }}
                            />
                        </div>
                    </div>
                </div>
            }
        />
        <FormSection
            label={"Adresse"}
            children={
                <>
                    <div className={"columns"}>
                        <div className={"column is-one-third"}>
                            <FieldInput
                                label={"Straße"}
                                disabled={saving}
                                required={true}
                                invalid={Boolean(addressStreetHelp)}
                                help={addressStreetHelp}
                                value={addressStreet}
                                onBlur={async () => await validate()}
                                onChange={addressStreet => {
                                    onChange({
                                        ...data,
                                        addressStreet
                                    })
                                }}
                            />
                        </div>
                        <div className={"column is-one-third"}>
                            <FieldInput
                                label={"Hausnummer"}
                                disabled={saving}
                                required={true}
                                invalid={Boolean(addressHouseNumberHelp)}
                                help={addressHouseNumberHelp}
                                value={addressHouseNumber}
                                onBlur={async () => await validate()}
                                onChange={addressHouseNumber => {
                                    onChange({
                                        ...data,
                                        addressHouseNumber
                                    })
                                }}
                            />
                        </div>
                        <div className={"column is-one-third"}>
                            <FieldInput
                                label={"Zusatz"}
                                disabled={saving}
                                required={false}
                                invalid={Boolean(addressAdditionHelp)}
                                help={addressAdditionHelp}
                                value={addressAddition}
                                onBlur={async () => await validate()}
                                onChange={addressAddition => {
                                    onChange({
                                        ...data,
                                        addressAddition
                                    })
                                }}
                            />
                        </div>
                    </div>
                    <div className={"columns"}>
                        <div className={"column is-one-third"}>
                            <FieldInput
                                label={"Postleitzahl"}
                                disabled={saving}
                                required={true}
                                invalid={Boolean(addressPostCodeHelp)}
                                help={addressPostCodeHelp}
                                value={addressPostCode}
                                onBlur={async () => await validate()}
                                onChange={addressPostCode => {
                                    onChange({
                                        ...data,
                                        addressPostCode
                                    })
                                }}
                            />
                        </div>
                        <div className={"column is-one-third"}>
                            <FieldInput
                                label={"Ort"}
                                disabled={saving}
                                required={true}
                                invalid={Boolean(addressCityHelp)}
                                help={addressCityHelp}
                                value={addressCity}
                                onBlur={async () => await validate()}
                                onChange={addressCity => {
                                    onChange({
                                        ...data,
                                        addressCity
                                    })
                                }}
                            />
                        </div>
                        <div className={"column is-one-third"}>
                            <FieldSelect
                                label={"Land"}
                                disabled={saving}
                                invalid={Boolean(addressCountryHelp)}
                                help={addressCountryHelp}
                                options={countryStore.options}
                                value={addressCountry}
                                onBlur={async () => await validate()}
                                onChange={addressCountry => {
                                    onChange({
                                        ...data,
                                        addressCountry
                                    })
                                }}
                                // onBlur={async () => await validate()}
                                required={true}
                            />
                        </div>
                    </div>
                </>
            }
        />
    </div>
})

export {
    CmsForm
}
